// ============================================================================
// LIGA AJUPAM — SurveyView (v6.7.0)
// ============================================================================
// Modal que renderiza la encuesta de una fase para el usuario logueado.
// Carga las preguntas desde user_get_active_surveys y envía con user_submit_survey.
//
// Props:
//   - phase: 'REGULAR' | 'PLAYOFFS'
//   - onClose: cerrar sin enviar
//   - onSubmitted: callback tras enviar exitosamente. Recibe el is_anonymous final.
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { BottomSheet } from './ui/BottomSheet';
import { Button } from './ui/Button';
import { ClipboardList, Send, Loader, ShieldCheck } from 'lucide-react';
import { Skeleton } from './ui/Skeleton';
import {
  getActiveSurveys,
  submitSurvey,
  SurveyPhase,
  SurveyQuestion as SurveyQuestionT,
} from '../services/api/surveys';
import { getCurrentSession } from '../services/api/auth';
import {
  SurveyQuestion,
  SurveyAnswerValue,
  initialAnswerForQuestion,
  answerValueToPayload,
  isAnswerComplete,
  selectedOptionsRequiringText,
} from './SurveyQuestion';

interface SurveyViewProps {
  phase: SurveyPhase;
  onClose: () => void;
  onSubmitted?: (info: { isAnonymous: boolean; phase: SurveyPhase }) => void;
  /** Cuando true, el modal no puede cerrarse hasta enviar la encuesta. */
  blocking?: boolean;
}

export const SurveyView: React.FC<SurveyViewProps> = ({ phase, onClose, onSubmitted, blocking = false }) => {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<SurveyQuestionT[]>([]);
  const [answers, setAnswers] = useState<Record<string, SurveyAnswerValue>>({});
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingIds, setMissingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const session = getCurrentSession();
        if (!session?.token) {
          setError('Sesión no encontrada. Volvé a iniciar sesión.');
          return;
        }
        const data = await getActiveSurveys(session.token);
        const phaseInfo = data.phases.find((p) => p.phase === phase);
        if (!phaseInfo) {
          setError('La encuesta no está disponible.');
          return;
        }
        if (phaseInfo.already_submitted) {
          setError('Ya completaste esta encuesta.');
          return;
        }
        if (!phaseInfo.is_open) {
          setError('La encuesta no está abierta en este momento.');
          return;
        }
        if (!phaseInfo.user_can_access) {
          setError('No tenés acceso a esta fase de la encuesta.');
          return;
        }
        setQuestions(phaseInfo.questions);
        const initial: Record<string, SurveyAnswerValue> = {};
        phaseInfo.questions.forEach((q) => {
          initial[q.id] = initialAnswerForQuestion(q);
        });
        setAnswers(initial);
      } catch (e: any) {
        console.error('Error cargando encuesta:', e);
        setError(e.message || 'Error al cargar la encuesta');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [phase]);

  const handleChange = (qId: string, value: SurveyAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));
    if (missingIds.has(qId)) {
      const next = new Set(missingIds);
      next.delete(qId);
      setMissingIds(next);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    // Validar required + el texto que exigen ciertas opciones (v6.11.128.02)
    const missing = new Set<string>();
    let missingExplanation = false;
    questions.forEach((q) => {
      const value = answers[q.id];
      if (!isAnswerComplete(q, value)) {
        missing.add(q.id);
        if (selectedOptionsRequiringText(q, value).length > 0) missingExplanation = true;
      }
    });
    if (missing.size > 0) {
      setMissingIds(missing);
      setError(
        missingExplanation
          ? 'Marcaste una opción que necesita una explicación breve.'
          : `Faltan ${missing.size} respuestas obligatorias.`
      );
      return;
    }

    try {
      setSubmitting(true);
      const session = getCurrentSession();
      if (!session?.token) throw new Error('Sesión no encontrada');

      const payload = questions
        .map((q) => answerValueToPayload(q, answers[q.id]))
        .filter((p) => Object.keys(p).length > 1); // mantener solo los con respuesta

      await submitSurvey(session.token, phase, payload as any, isAnonymous);
      onSubmitted?.({ isAnonymous, phase });
      onClose();
    } catch (e: any) {
      console.error('Error enviando encuesta:', e);
      setError(e.message || 'Error al enviar la encuesta');
    } finally {
      setSubmitting(false);
    }
  };

  const phaseLabel = phase === 'REGULAR' ? 'Fase Regular' : 'Playoffs';

  // Agrupar por categoría para mejor UX
  const grouped = useMemo(() => {
    const map = new Map<string, SurveyQuestionT[]>();
    questions.forEach((q) => {
      if (!map.has(q.category)) map.set(q.category, []);
      map.get(q.category)!.push(q);
    });
    return Array.from(map.entries());
  }, [questions]);

  return (
    <BottomSheet
      isOpen={true}
      onClose={blocking ? () => {} : onClose}
      maxWidth="lg"
      showCloseButton={!blocking}
      preventClose={blocking}
      title={
        <div className="flex items-center gap-2">
          <ClipboardList size={22} className="text-primary" />
          <h2 className="font-display font-bold text-lg">
            {blocking ? 'Tu opinión sobre la ' : 'Encuesta '}
          {blocking ? phaseLabel : phaseLabel}
          </h2>
        </div>
      }
    >
      <div className="space-y-6">
        {loading ? (
          <div className="space-y-6 animate-pulse">
            {/* Espeja los grupos de preguntas: título de categoría + filas de opción */}
            {[0, 1, 2].map((g) => (
              <div key={g} className="space-y-4">
                <Skeleton variant="text" className="h-4 w-3/4" />
                {[0, 1, 2, 3].map((r) => (
                  <Skeleton key={r} className="h-10" />
                ))}
              </div>
            ))}
          </div>
        ) : error && questions.length === 0 ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-600">
              Tu opinión nos ayuda a mejorar. Las respuestas marcadas con <span className="text-red-500 font-bold">*</span> son obligatorias.
            </p>

            {grouped.map(([cat, qs]) => (
              <div key={cat} className="space-y-4">
                {grouped.length > 1 && (
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-primary/70">
                    {cat.replace(/_/g, ' ')}
                  </h3>
                )}
                {qs.map((q) => (
                  <SurveyQuestion
                    key={q.id}
                    question={q}
                    value={answers[q.id]}
                    onChange={(v) => handleChange(q.id, v)}
                    showError={missingIds.has(q.id)}
                  />
                ))}
              </div>
            ))}

            {/* Toggle anonimato */}
            <div className="border-t pt-4 space-y-2">
              <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-gray-600" />
                    <span className="text-sm font-bold text-text-main">Enviar de forma anónima</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Si lo activás, el admin no verá tu nombre ni el de tu equipo, pero sí
                    la rama y la categoría en la que jugás (esa información nos ayuda a entender mejor el feedback).
                  </p>
                </div>
              </label>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                leftIcon={submitting ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                className="flex-1"
              >
                {submitting ? 'Enviando...' : 'Enviar respuesta'}
              </Button>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
};
